import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Store, Package, ScanLine, Award, BookOpen, Loader2, CheckCircle2 } from "lucide-react";
import { api, apiError } from "../lib/api";
import { rupiah } from "../lib/format";
import { useAuth } from "../context/AuthContext";

const STATUS_LABEL = {
  PLACED: "Pesanan Dibuat", PURCHASED: "Dibeli dari Supplier", GUANGZHOU_HUB: "Gudang Guangzhou",
  CUSTOMS: "Bea Cukai", INDO_WAREHOUSE: "Gudang Indonesia", DISPATCHED_KEDAI: "Dikirim ke Kedai",
  READY_PICKUP: "Siap Diambil", COMPLETED: "Selesai",
};

export default function KioskPanel() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [kiosk, setKiosk] = useState(null);
  const [codes, setCodes] = useState({});
  const [loading, setLoading] = useState({});

  const load = () => {
    api.get("/orders").then((r) => setOrders(r.data.orders));
    api.get("/kiosks", { params: { all: true } }).then((r) => setKiosk(r.data.kiosks.find((k) => k.id === user.kiosk_id)));
  };
  useEffect(load, [user]);

  const verify = async (id) => {
    setLoading((l) => ({ ...l, [id]: true }));
    try {
      await api.post(`/orders/${id}/verify-pickup`, { code: codes[id] || "" });
      toast.success("Pengambilan terverifikasi! Pesanan selesai.");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setLoading((l) => ({ ...l, [id]: false })); }
  };

  const ready = orders.filter((o) => ["DISPATCHED_KEDAI", "READY_PICKUP"].includes(o.status));
  const done = orders.filter((o) => o.status === "COMPLETED");

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <div className="flex items-center gap-3 mb-2"><Store className="text-secondary" size={26} /><h1 className="font-display text-2xl font-bold text-stone-900">Panel Kedai</h1></div>
      {kiosk && <p className="text-stone-500 mb-6">{kiosk.name} — {kiosk.subdistrict}, {kiosk.city}</p>}

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-2xl border border-stone-200 p-5"><Package className="text-emerald-600 mb-2" size={22} /><p className="text-2xl font-display font-bold">{ready.length}</p><p className="text-sm text-stone-500">Paket menunggu diambil</p></div>
        <div className="bg-white rounded-2xl border border-stone-200 p-5"><CheckCircle2 className="text-emerald-600 mb-2" size={22} /><p className="text-2xl font-display font-bold">{done.length}</p><p className="text-sm text-stone-500">Sudah diambil warga</p></div>
        <div className="bg-white rounded-2xl border border-stone-200 p-5"><Award className="text-amber-500 mb-2" size={22} /><p className="text-2xl font-display font-bold">{kiosk?.points ?? 0}</p><p className="text-sm text-stone-500">Poin & bonus kedai</p></div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-8 flex gap-3">
        <BookOpen className="text-amber-600 shrink-0" size={22} />
        <div><h3 className="font-semibold text-amber-800 text-sm">Edukasi & Bantu Pesan Warga</h3><p className="text-sm text-amber-700/80 mt-0.5">Bantu warga cari barang pakai Bahasa Indonesia, jelaskan harga sudah Rupiah, dan buatkan pesanan atas nama mereka. Warga cukup datang mengambil barang di kedai.</p></div>
      </div>

      <h2 className="font-display font-semibold text-lg text-stone-900 mb-3 flex items-center gap-2"><ScanLine size={20} />Verifikasi Pengambilan</h2>
      {ready.length === 0 ? (
        <p className="text-stone-400 text-sm mb-8" data-testid="kiosk-no-ready">Belum ada paket yang siap diambil di kedai ini.</p>
      ) : (
        <div className="space-y-3 mb-8">
          {ready.map((o) => (
            <div key={o.id} data-testid={`kiosk-order-${o.id}`} className="bg-white rounded-2xl border border-stone-200 p-4 flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[180px]">
                <p className="font-mono text-xs text-stone-400">{o.awb}</p>
                <p className="text-sm font-medium text-stone-800">{o.user_name} · {o.items.length} barang · {rupiah(o.total_idr)}</p>
                <span className="text-xs font-semibold text-emerald-700">{STATUS_LABEL[o.status]}</span>
              </div>
              <input data-testid={`kiosk-code-${o.id}`} value={codes[o.id] || ""} onChange={(e) => setCodes((c) => ({ ...c, [o.id]: e.target.value }))}
                placeholder="Kode ambil" className="border border-stone-200 rounded-lg px-3 py-2 text-sm w-32 uppercase outline-none focus:border-emerald-400" />
              <button data-testid={`kiosk-verify-${o.id}`} onClick={() => verify(o.id)} disabled={loading[o.id]} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-60">
                {loading[o.id] ? <Loader2 className="animate-spin" size={15} /> : <CheckCircle2 size={15} />}Verifikasi
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
